import React from 'react';
import MaskedInput from 'react-text-mask';
import useStyles from './TablePaging.styles';

import { useState, useEffect, FormEvent, ChangeEvent, useCallback } from 'react';
import {
  Box,
  Input,
  Tooltip,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import { Check, LastPage, FirstPage, ChevronLeft, ChevronRight } from '@material-ui/icons';
import { createNumberMask } from 'text-mask-addons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';

const integerMask = createNumberMask({
  prefix: '',
  allowLeadingZeroes: false,
  includeThousandsSeparator: false,
});

function Masked({ inputRef, mask, ...other }: any) {
  return (
    <MaskedInput
      {...(other as any)}
      mask={integerMask}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      guide={false}
      showMask={true}
      placeholderChar={'\u2000'}
    />
  );
}

interface IProps {
  count: number;
  page?: number;
  rowsPerPage: number;
  changePage: (page: number) => void;
}

const TablePaging = ({ count, page, changePage, rowsPerPage }: IProps) => {
  const [pageText, setPageText] = useState(`${page ?? 1}`);
  const styles = useStyles();
  const loading = false;
  const totalPages = Math.ceil(count / rowsPerPage);
  const parsedPage = parseInt(pageText);
  const pageTextValid = parsedPage >= 0 && parsedPage < totalPages;

  const { t } = useTranslation();

  const pageTo = (pageNumber: number) => () => changePage(pageNumber);
  const pageToText = useCallback(() => {
    if (pageTextValid) {
      changePage(parsedPage);
    }
  }, [changePage, pageTextValid, parsedPage]);

  const handlePageTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPageText(event.target.value);
  }, []);
  const handleFormSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      pageToText();
    },
    [pageToText],
  );

  useEffect(() => {
    setPageText(`${page ?? 1}`);
  }, [page]);

  const itemFrom = ((page || 1) - 1) * rowsPerPage + 1;
  const predictedTo = itemFrom + rowsPerPage - 1;
  const itemTo = predictedTo > count ? count : predictedTo;
  const totalItems = count === Infinity ? '∞' : count;

  const hasPrev = (page ?? 1) > 0;
  const hasNext = (page ?? 1) < totalPages;
  const samePage = `${page ?? 1}` === pageText;
  return (
    <Box className={styles.wrapper}>
      <Typography variant="body2" color="textSecondary" className={styles.text}>
        {totalItems === 0
          ? t(tPaths.tablePaging.noRecords)
          : `${itemFrom}-${itemTo} ${t(tPaths.tablePaging.of)} ${count}`}
      </Typography>
      {loading && <CircularProgress size="1.5em" data-qa="TableProgress" />}
      <div className={styles.spacer} />
      {totalPages > 0 && (
        <Typography
          variant="body2"
          color="textSecondary"
          className={clsx(styles.text, styles.rightItems)}
        >
          {`${t(tPaths.tablePaging.page)} ${page ?? 1} ${t(tPaths.tablePaging.of)} ${totalPages}`}
        </Typography>
      )}
      <Tooltip title={t(tPaths.tablePaging.firstPage) as string}>
        <div>
          <IconButton size="small" onClick={pageTo(1)} disabled={!hasPrev}>
            <FirstPage />
          </IconButton>
        </div>
      </Tooltip>
      <Tooltip title={t(tPaths.tablePaging.prevPage) as string}>
        <div>
          <IconButton size="small" onClick={pageTo((page ?? 1) - 1)} disabled={!hasPrev}>
            <ChevronLeft />
          </IconButton>
        </div>
      </Tooltip>
      <Box>
        <form onSubmit={handleFormSubmit}>
          <Input
            value={pageText ?? 0}
            className={styles.pageInputWrapper}
            classes={{ input: styles.pageInput }}
            onChange={handlePageTextChange}
            inputComponent={Masked}
            endAdornment={
              !samePage && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={pageToText} disabled={!pageTextValid}>
                    <Check />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        </form>
      </Box>
      <Tooltip title={t(tPaths.tablePaging.nextPage) as string}>
        <div>
          <IconButton size="small" onClick={pageTo((page ?? 1) + 1)} disabled={!hasNext}>
            <ChevronRight />
          </IconButton>
        </div>
      </Tooltip>
      <Tooltip title={t(tPaths.tablePaging.lastPage) as string}>
        <div>
          <IconButton size="small" onClick={pageTo(totalPages)} disabled={!hasNext}>
            <LastPage />
          </IconButton>
        </div>
      </Tooltip>
    </Box>
  );
};

export default TablePaging;
